import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getCheckoutCreatorStore, SetIsEmailTemplateOpen, SetTemplatePreview } from '@/store/checkoutCreator/actions';
import { TemplatePreviewButton } from '@/assets/TemplatePreviewButton';
import { EditTemplateIcon, Typography, ViewTemplateIcon } from '@dev/ee-components';
import { getApiPath } from '@/services/Secuirty';
import { useContactListsNamesMap } from '@/services/hooks/useContactListsNamesMap';
import { Step5ContentAudience, Step5ContentColumns, Step5ContentField, Step5ContentFields, Step5ContentScreenshotHover, Step5ContentScreenshotWrapper, Step5ContentWrapper } from './styles';

interface Step5ContentProps {
    isEditAvailable: boolean;
}

export const Step5Content = ({ isEditAvailable }: Step5ContentProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const contactListsMap = useContactListsNamesMap();
    const { checkoutObject } = useSelector(getCheckoutCreatorStore);

    const audicenceLists = checkoutObject?.CheckoutAudience?.map((id) => <Typography variant="body1" key={id}>{contactListsMap.get(id)}</Typography>);

    const templateScreenshot = checkoutObject?.PostPurchaseTemplate?.Screenshot;

    const editTemplate = (): void => {
        dispatch(SetIsEmailTemplateOpen(true));
    };
    const viewTemplate = (): void => {
        const previewHtmlContent = checkoutObject?.PostPurchaseTemplate?.Body?.Parts?.find((part) => part.ContentType === 'text/html')?.Content;
        dispatch(SetTemplatePreview(previewHtmlContent));
    };

    return (
        <Step5ContentWrapper>
            <Step5ContentColumns>
                <div>
                    <Step5ContentScreenshotWrapper>
                        <Step5ContentScreenshotHover>
                            {isEditAvailable && <TemplatePreviewButton
                                id="checkout-edit-post-purchase-template"
                                buttonIcon={<EditTemplateIcon width={31.5} height={31.5} />}
                                buttonLabel={t('edit')}
                                handleOnClick={editTemplate}
                            />}
                            <TemplatePreviewButton
                                id="checkout-view-post-purchase-template"
                                buttonIcon={<ViewTemplateIcon width={28.8} height={28.8} />}
                                buttonLabel={t('view')}
                                handleOnClick={viewTemplate}
                            />
                        </Step5ContentScreenshotHover>
                        {templateScreenshot && <img src={`${getApiPath()}/userfile${encodeURI(templateScreenshot)}`} />}
                    </Step5ContentScreenshotWrapper>
                </div>
                <Step5ContentFields>
                    <Step5ContentField>
                        <Typography variant="h3">{t('fromname')}</Typography>
                        <Typography variant="body1">{checkoutObject?.PostPurchaseTemplate?.FromName ?? ''}</Typography>
                    </Step5ContentField>
                    <Step5ContentField>
                        <Typography variant="h3">{t('frommail')}</Typography>
                        <Typography variant="body1">{checkoutObject?.PostPurchaseTemplate?.FromEmail ?? ''}</Typography>
                    </Step5ContentField>
                    <Step5ContentField>
                        <Typography variant="h3">{t('subject')}</Typography>
                        <Typography variant="body1">{checkoutObject?.PostPurchaseTemplate?.Body?.Subject ?? ''}</Typography>
                    </Step5ContentField>
                </Step5ContentFields>
            </Step5ContentColumns>
            <Step5ContentAudience>
                <Typography variant="h3">{t('audience')}</Typography>
                {audicenceLists}
            </Step5ContentAudience>
        </Step5ContentWrapper>
    );
};
